import {
  createEmptyParcel,
  updateParcelWeights,
} from '../../../components/calculator/utils/calculationUtils';
import { ShippingType } from '../../../components/calculator/components';
import { ExtendedDocument } from '../types';
import { isFedexProvider, normalizeProviderCode } from '../constants/shippingOptions';
import { useShippingWizard } from './useShippingWizard';

type WizardHook = ReturnType<typeof useShippingWizard>;

export function useShippingHandlers(w: WizardHook) {
  const handleAddParcel = () => {
    const newParcel = createEmptyParcel();
    if (w.parcelDetails.packageType === 'document') {
      newParcel.weight = 0.5;
      newParcel.length = 0;
      newParcel.width = 0;
      newParcel.height = 0;
    }
    const updatedParcel = updateParcelWeights(newParcel, w.unitSystem);
    const selectedSvc = w.shippingRates.find((rate) => rate.id === w.selectedRateId);
    const isFedex = isFedexProvider(selectedSvc?.provider, selectedSvc?.providerCode);
    w.setParcels((prev) => [
      ...prev,
      {
        ...updatedParcel,
        showPrice: w.parcelDetails.packageType === 'package',
        fedexPackagingType: isFedex ? 'YOUR_PACKAGING' : undefined,
      },
    ]);
    w.setShouldRefreshRates(true);
  };

  const handleRemoveParcel = (id: string) => {
    w.setParcels((prev) => {
      const updatedParcels = prev.filter((parcel) => parcel.id !== id);
      if (updatedParcels.length === 0) {
        updatedParcels.push({
          ...updateParcelWeights(createEmptyParcel(), w.unitSystem),
          showPrice: true,
        });
      }
      return updatedParcels;
    });
    w.setParcelErrors((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
    w.setShouldRefreshRates(true);
  };

  const handleUpdateParcel = (id: string, field: string, value: number | string) => {
    const updatedParcels = [...w.parcels];
    const parcelIndex = updatedParcels.findIndex((p) => p.id === id);
    if (parcelIndex === -1) return;

    if (field === 'fedexPackagingType') {
      updatedParcels[parcelIndex] = { ...updatedParcels[parcelIndex], fedexPackagingType: value as string };
      w.setParcels(updatedParcels);
      w.setShouldRefreshRates(true);
      return;
    }

    if (field === 'items') {
      const parcelFormItems = value as unknown as Array<{
        id: string; description: string; quantity: number; weight: number; price: number; madeIn: string;
      }>;
      updatedParcels[parcelIndex] = { ...updatedParcels[parcelIndex], items: parcelFormItems };
      w.setParcels(updatedParcels);
      w.setShouldRefreshRates(true);
      return;
    }

    updatedParcels[parcelIndex] = { ...updatedParcels[parcelIndex], [field]: value };
    const updatedParcel = updateParcelWeights(updatedParcels[parcelIndex], w.unitSystem);
    updatedParcels[parcelIndex] = {
      ...updatedParcel,
      showPrice: w.parcelDetails.packageType === 'package',
      fedexPackagingType: updatedParcels[parcelIndex].fedexPackagingType,
    };
    w.setParcels(updatedParcels);
    w.setShouldRefreshRates(true);
  };

  const handleAddDocument = () => {
    const newDocument: ExtendedDocument = {
      id: `document-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      quantity: 1,
      weight: 0.5,
      size: 'large_envelope',
    };
    w.setDocuments((prev) => [...prev, newDocument]);
    w.setShouldRefreshRates(true);
  };

  const handleRemoveDocument = (id: string) => {
    if (w.documents.length <= 1) return;
    w.setDocuments((prev) => prev.filter((doc) => doc.id !== id));
    w.setShouldRefreshRates(true);
  };

  const handleUpdateDocument = (id: string, field: string, value: any) => {
    w.setDocuments((prev) =>
      prev.map((doc) => (doc.id === id ? { ...doc, [field]: value } : doc))
    );
    w.setShouldRefreshRates(true);
  };

  const handleVatTypeChange = (value: string) => w.setVatType(value);
  const handleVatNumberChange = (value: string) => w.setVatNumber(value);

  const handleShippingTypeChange = (newType: ShippingType) => {
    w.setParcelDetails((prev) => ({ ...prev, packageType: newType }));
    if (w.locationState) {
      w.setLocationState((prev: any) => ({ ...prev, shippingType: newType }));
    }
    if (newType === 'document' && w.documents.length === 0) {
      w.setDocuments([
        {
          id: `document-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          quantity: 1,
          weight: 0.5,
          size: 'large_envelope',
        },
      ]);
    }
    if (newType === 'package' && w.parcels.length === 0) {
      const newParcel = createEmptyParcel();
      const updatedParcel = updateParcelWeights(newParcel, w.unitSystem);
      w.setParcels([{ ...updatedParcel, showPrice: true }]);
    }
    w.setShouldRefreshRates(true);
  };

  const handleServiceSelect = (serviceId: string) => {
    w.setErrorMessage('');
    w.setDestinationPostalCodeError(false);
    w.setSelectedRateId(serviceId);
    w.setShouldRefreshRates(false);

    const newSelectedService = w.shippingRates.find((rate) => rate.id === serviceId);
    const newProvider = newSelectedService?.provider;
    const newProviderCode = newSelectedService?.providerCode;

    if (newProvider) w.setCurrentProvider(newProvider);
    if (newProviderCode) {
      w.setCurrentProviderCode(newProviderCode);
    } else if (newProvider) {
      w.setCurrentProviderCode(normalizeProviderCode(newProvider));
    }

    if (w.locationState) {
      w.setLocationState((prev: any) => ({
        ...prev,
        selectedRateProvider: newProvider,
        selectedRateProviderCode:
          newProviderCode || prev?.selectedRateProviderCode || normalizeProviderCode(newProvider),
      }));
    }

    const isFedex = isFedexProvider(newProvider, newProviderCode);
    if (isFedex) {
      w.setParcels((prev) =>
        prev.map((parcel) => ({
          ...parcel,
          fedexPackagingType: parcel.fedexPackagingType || 'YOUR_PACKAGING',
        }))
      );
    }

    w.setParcelErrors((prevErrors) => {
      const updatedErrors: Record<string, Record<string, boolean>> = {};
      for (const parcelId in prevErrors) {
        updatedErrors[parcelId] = { ...prevErrors[parcelId] };
        delete updatedErrors[parcelId].weightLimit;
        if (Object.keys(updatedErrors[parcelId]).length === 0) delete updatedErrors[parcelId];
      }
      return updatedErrors;
    });
  };

  return {
    handleAddParcel,
    handleRemoveParcel,
    handleUpdateParcel,
    handleAddDocument,
    handleRemoveDocument,
    handleUpdateDocument,
    handleVatTypeChange,
    handleVatNumberChange,
    handleShippingTypeChange,
    handleServiceSelect,
  };
}
